import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Gift, Check, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { PageHeader } from "../components/PageHeader";
import { Seo } from "../components/Seo";
import api, { formatApiError } from "../lib/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const PRESETS = [50, 100, 250, 500];

export default function GiftCards() {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const [clientSecret, setClientSecret] = useState(null);
  const [form, setForm] = useState({ amount: 100, custom: "", recipient_name: "", recipient_email: "", sender_name: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/giftcards/status/${sessionId}`);
        if (data.payment_status === "paid" && data.gift_card) { setResult(data.gift_card); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 10) setTimeout(poll, 1500);
      else setResult({ pending: true });
    };
    poll();
  }, [sessionId]);

  const amount = form.custom ? parseFloat(form.custom) : form.amount;

  const startPayment = async (e) => {
    e.preventDefault();
    if (!amount || amount < 25 || amount > 1000) { toast.error("Amount must be between $25 and $1000"); return; }
    if (!form.recipient_email.trim()) { toast.error("Enter the recipient's email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/giftcards/checkout", {
        amount, recipient_name: form.recipient_name, recipient_email: form.recipient_email,
        sender_name: form.sender_name, message: form.message, origin_url: window.location.origin,
      });
      setClientSecret(data.client_secret);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout"); }
    finally { setBusy(false); }
  };

  // --- Success state (returned from Stripe) ---
  if (sessionId) {
    return (
      <div data-testid="giftcard-success-page">
        <Seo title="Gift Card" path="/gift-cards" />
        <PageHeader overline="Gift Cards" title="Thank you!" subtitle="Your gift card purchase is complete." />
        <div className="max-w-lg mx-auto px-6 pb-24 text-center">
          {!result ? (
            <div className="flex flex-col items-center gap-3 text-[#8A9492]" data-testid="giftcard-processing">
              <Loader2 className="h-6 w-6 animate-spin" /> Confirming your payment…
            </div>
          ) : result.pending ? (
            <p className="text-[#5C6664]">Your payment is processing. The gift card will be emailed to the recipient shortly.</p>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 p-8" data-testid="giftcard-issued">
              <div className="mx-auto h-12 w-12 rounded-full iri-gradient flex items-center justify-center mb-4"><Check className="h-6 w-6 text-ink" /></div>
              <p className="text-[#5C6664]">A <strong>${Number(result.initial_amount).toFixed(0)}</strong> gift card has been emailed to <strong>{result.recipient_email}</strong>.</p>
              <div className="mt-4 rounded-xl bg-stone p-4">
                <p className="text-xs uppercase tracking-wider text-[#8A9492]">Gift card code</p>
                <p className="font-mono text-xl font-medium text-ink mt-1" data-testid="giftcard-code">{result.code}</p>
              </div>
            </div>
          )}
          <Button onClick={() => setParams({})} data-testid="giftcard-back" className="mt-6 rounded-full iri-gradient text-ink gap-2"><ArrowLeft className="h-4 w-4" /> Buy another</Button>
        </div>
      </div>
    );
  }

  // --- Embedded checkout state ---
  if (clientSecret) {
    return (
      <div data-testid="giftcard-checkout-page">
        <Seo title="Gift Card Checkout" path="/gift-cards" />
        <PageHeader overline="Gift Cards" title="Complete your purchase" subtitle="Secure payment — you never leave our site." />
        <div className="max-w-2xl mx-auto px-6 pb-24">
          <button onClick={() => setClientSecret(null)} data-testid="giftcard-checkout-back" className="mb-4 text-sm text-[#8A9492] hover:text-ink flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back</button>
          <div id="checkout" data-testid="embedded-checkout">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      </div>
    );
  }

  // --- Purchase form ---
  return (
    <div data-testid="giftcards-page">
      <Seo title="Gift Cards" description="Give the gift of glow — buy an Iridescent Medical & Wellness Spa gift card online. Redeemable toward treatments, services and products in Clinton Township, MI." path="/gift-cards" />
      <PageHeader overline="Gift Cards" title="Give the gift of glow" subtitle="A digital gift card delivered straight to their inbox — redeemable toward treatments, services and products." />
      <div className="max-w-xl mx-auto px-6 pb-24">
        <form onSubmit={startPayment} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
          <div>
            <label className="text-sm font-medium text-ink flex items-center gap-2"><Gift className="h-4 w-4 text-iri-mid" /> Choose an amount</label>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {PRESETS.map((p) => (
                <button type="button" key={p} onClick={() => setForm({ ...form, amount: p, custom: "" })} data-testid={`giftcard-amount-${p}`}
                  className={`rounded-xl border py-3 text-sm font-medium transition-colors ${!form.custom && form.amount === p ? "border-iri-mid bg-stone text-ink" : "border-slate-200 text-[#5C6664] hover:border-slate-300"}`}>
                  ${p}
                </button>
              ))}
            </div>
            <Input type="number" placeholder="Or enter a custom amount ($25–$1000)" value={form.custom}
              onChange={(e) => setForm({ ...form, custom: e.target.value })} data-testid="giftcard-custom-amount" className="mt-3 rounded-lg h-11" />
          </div>
          <div className="space-y-3">
            <Input required type="email" placeholder="Recipient email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} data-testid="giftcard-recipient-email" className="rounded-lg h-11" />
            <Input placeholder="Recipient name (optional)" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="giftcard-recipient-name" className="rounded-lg h-11" />
            <Input placeholder="Your name (optional)" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} data-testid="giftcard-sender-name" className="rounded-lg h-11" />
            <Textarea placeholder="Add a personal message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="giftcard-message" rows={3} />
          </div>
          <Button type="submit" disabled={busy} data-testid="giftcard-continue" className="w-full iri-gradient text-ink rounded-full h-12 font-medium">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Continue to payment · $${amount ? Number(amount).toFixed(0) : "0"}`}
          </Button>
        </form>
      </div>
    </div>
  );
}
